import React, { useCallback, useEffect, useRef, useState } from "react";
import { GridCellProps } from "@progress/kendo-react-grid";
import styles from "styles.module.css";
import { ICON_NAMES } from "utils/constant";
import Filter from "./Filter";
import { PageTypePath } from "utils/pages";
import { GridPDFExport } from "@progress/kendo-react-pdf";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { addMonths } from "date-fns";
import OverLayLoading from "ui/Loading";
import GridCustomPage from "ui/grid/GridPage";
import { useTranslation } from "react-i18next";
import { releasedPackageGridColumnItem } from "./GridColumns";
import { getReleasedPackagesApi, ReleasedPackageFilter } from "api/package";
import { ReleasePackage } from "interface/package";
import ReleasedPackagesMobileList from "./ReleasedPackagesMobileList";
import "./styles.css";

const pageSize = 60;

const ReleasedPackagePage: React.FC<{}> = ({}) => {
	const { t } = useTranslation();
	const overlayRef = useRef<HTMLDivElement>(null);
	const mobileRequestInProgress = useRef(false);
	const mobileDataFinished = useRef(false);
	const mobileNextSkip = useRef(0);
	const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
	const [mobileData, setMobileData] = useState<ReleasePackage[]>([]);
	const [mobileLoading, setMobileLoading] = useState(false);

	const [filterOptions, setFilterOptions] = useState<ReleasedPackageFilter>({
		take: pageSize,
		skip: 0,
		fromDate: addMonths(new Date(), -1),
		toDate: new Date(),
	});

	const pdfRef = useRef<GridPDFExport>(null);
	const excelRef = useRef<ExcelExport>(null);

	const exportGridAsPDF = () => {
		if (pdfRef.current !== null) {
			pdfRef.current.save();
		}
	};

	const exportGridAsExcel = () => {
		if (excelRef.current !== null) {
			excelRef.current.save();
		}
	};

	const requestMobileData = useCallback(
		(skip: number, take: number, resetData = false) => {
			if (mobileRequestInProgress.current || mobileDataFinished.current) {
				return;
			}

			mobileRequestInProgress.current = true;
			setMobileLoading(true);

			getReleasedPackagesApi({
				...filterOptions,
				skip,
				take,
			})
				.then((response) => {
					const incoming = (response?.data || []) as ReleasePackage[];

					if (incoming.length === 0) {
						mobileDataFinished.current = true;
						if (resetData) {
							setMobileData([]);
						}
						return;
					}

					mobileNextSkip.current = skip + take;

					if (resetData) {
						setMobileData(incoming);
					} else {
						setMobileData((prev) => [...prev, ...incoming]);
					}

					const loadedCount = skip + incoming.length;
					if (loadedCount >= (response?.totalCount || 0)) {
						mobileDataFinished.current = true;
					}
				})
				.finally(() => {
					mobileRequestInProgress.current = false;
					setMobileLoading(false);
				});
		},
		[filterOptions],
	);

	useEffect(() => {
		const updateMobileView = () => setIsMobileView(window.innerWidth < 768);
		window.addEventListener("resize", updateMobileView);

		return () => {
			window.removeEventListener("resize", updateMobileView);
		};
	}, []);

	useEffect(() => {
		if (!isMobileView) {
			return;
		}

		mobileDataFinished.current = false;
		mobileNextSkip.current = 0;
		setMobileData([]);

		requestMobileData(0, pageSize, true);
	}, [isMobileView, requestMobileData]);

	const openPdf = (props: GridCellProps) => {
		window.open(`/${PageTypePath.Pdf}/${props.dataItem.reportGuid}`, "_blank");
		return;
	};

	const openPdfByItem = (item: ReleasePackage) => {
		const reportGuid =
			(item as Record<string, any>).reportGuid ??
			(item as Record<string, any>).ReportGuid;

		if (!reportGuid) {
			return;
		}

		window.open(`/${PageTypePath.Pdf}/${reportGuid}`, "_blank");
	};

	return (
		<div className={`${styles.PageContainer} released-package-page`}>
			<div ref={overlayRef} style={{ display: "none" }}>
				<OverLayLoading />
			</div>
			<div className="page-header">
				<legend className={styles.BoxHeadingLarge}>
					{t("ReleasedPackages")}
				</legend>
			</div>

			<div>
				<Filter
					setFilterOptions={setFilterOptions}
					filterOptions={filterOptions}
					exportAsPDF={exportGridAsPDF}
					exportAsExcel={exportGridAsExcel}
					pageSize={pageSize}
				/>
			</div>
			<div style={{ marginTop: 30, height: "100%" }}>
				{isMobileView ? (
					<ReleasedPackagesMobileList
						items={mobileData}
						loading={mobileLoading}
						onOpenPdf={openPdfByItem}
						onLoadMore={() =>
							requestMobileData(mobileNextSkip.current, pageSize)
						}
					/>
				) : (
					<GridCustomPage
						dataKey=""
						gridColumns={releasedPackageGridColumnItem()}
						exportGridAsPDFRef={pdfRef}
						exportGridAsExcelRef={excelRef}
						apiMethod={getReleasedPackagesApi}
						filter={filterOptions}
						lastColWidth="100px"
						showDetailIcon={false}
						excelProps={{
							fileName: `released_packages_${new Date().toISOString()}.xlsx`,
						}}
						lastColBtns={[
							{
								iconName: ICON_NAMES.Download,
								alwaysShow: true,
								onclick: openPdf,
							},
						]}
					/>
				)}
			</div>
		</div>
	);
};

export default ReleasedPackagePage;
